import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    effect,
    inject,
    signal
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, lastValueFrom, map, tap } from 'rxjs';
import Swal from 'sweetalert2';

import { BarcodeScannerDialog } from '../../../components/dialogs/barcode-scanner/barcode-scanner.dialog';
import { GateDeviceCodeDialogComponent } from '../../../components/dialogs/gate-device-code-dialog/gate-device-code-dialog.component';
import { DmiStatusIndicatorComponent } from '../../../components/dmi/dmi-status-indicator/dmi-status-indicator.component';
import { BaseComponent } from '../../../core/abstractions/base.component';
import { Tools } from '../../../core/utils/tools';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { PostValueResponseModel } from '../../../models/core/post-value-response.model';
import { GateActionTypeEnum } from '../../../models/enums/gate-action-type.enum';
import { GateLogModel } from '../../../models/gate-log.model';
import { GmAdmissionModel } from '../../../models/gm-admission.model';
import { OptionalType } from '../../../models/types/optional.type';
import { ErrorsService } from '../../../services/errors.service';
import { GateResource } from '../../../services/resources/gate.resource';
import { GateStorage } from '../../../services/storage/gate.storage';
import { TranslationService } from '../../../services/translation.service';
import { GateRegisterProxiesDialog } from './gate-register-proxies/gate-register-proxies.dialog';
import { RegisterProxiesResultModel } from './gate-register-proxies/models/register-proxy-result.model';

@Component({
    selector: 'sl-gm-gate',
    templateUrl: './gm-gate.component.html',
    styleUrls: ['./gm-gate.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GmGateComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('dmiStatusIndicator') dmiStatusIndicator: DmiStatusIndicatorComponent;
    @ViewChild('barcodeInput') barcodeInput: ElementRef<HTMLInputElement>;
    @ViewChild('logContainer') logContainer: ElementRef<HTMLDivElement>;

    GateActionTypeEnum = GateActionTypeEnum;

    token: string;
    barcode = '';
    hasMediaDevices = false;
    actionType: GateActionTypeEnum = GateActionTypeEnum.Checkin;
    log = signal<GateLogModel[]>([]);

    private readonly gateResource = inject(GateResource);
    private readonly gateStorage = inject(GateStorage);
    private readonly errorsService = inject(ErrorsService);
    private readonly matDialog = inject(MatDialog);
    private readonly translationService = inject(TranslationService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        super();

        // runs when the value of the log changes
        effect(
            () => {
                if (this.log().length > 0) {
                    this.gateStorage.saveLogs(this.log());
                }
                this.changeDetectorRef.detectChanges();
                this.logContainer.nativeElement.scrollTo(0, 0);
            },
            { allowSignalWrites: true }
        );
    }

    ngOnInit(): void {
        this.errorsService.shouldDisplayAlertOnError = false;
        const urlParams = new URLSearchParams(window.location.search);
        this.token = urlParams.get('api_key') ?? '';

        this.hasMediaDevices = navigator?.mediaDevices !== undefined;

        this.log.set(this.gateStorage.getLogs() ?? []);

        window.addEventListener('keydown', this.closeSwalDialog);
    }

    ngAfterViewInit(): void {
        Tools.Utils.startFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);
    }

    override ngOnDestroy(): void {
        this.errorsService.shouldDisplayAlertOnError = true;
        window.removeEventListener('keydown', this.closeSwalDialog);
    }

    openBarcodeScanDialog(): void {
        const dialogRef = this.matDialog.open(BarcodeScannerDialog, {
            width: '500px',
            height: '500px',
            maxWidth: '98vw',
            maxHeight: '98svh',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!!result) {
                this.barcode = result;
                this.submit();
            }
        });
    }

    async submit(): Promise<void> {
        if (!this.barcode || this.barcode.length === 0) {
            return;
        }

        Tools.Utils.endFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);

        if (this.actionType === GateActionTypeEnum.CheckinRepresentative) {
            const proxiesResult = await lastValueFrom(this.registerProxies());

            if (!proxiesResult) {
                this.afterSubmit();
                return;
            }
        }

        let dmiCode: OptionalType<string>;
        if (this.actionType !== GateActionTypeEnum.Checkout && this.isDmi()) {
            dmiCode = await lastValueFrom(this.askDmiCode());
            if (!dmiCode) {
                this.afterSubmit();
                return;
            }
        }

        this.sendRequest(this.barcode, dmiCode)
            .subscribe()
            .add(() => this.afterSubmit());
    }

    private afterSubmit(): void {
        this.barcode = '';
        Tools.Utils.startFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);
    }

    private registerProxies(): Observable<OptionalType<RegisterProxiesResultModel>> {
        Tools.Utils.endFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);
        const dialogRef = this.matDialog.open(GateRegisterProxiesDialog, {
            data: {
                representativeBarcode: this.barcode
            },
            width: '500px',
            maxWidth: '98vw',
            maxHeight: '98svh',
            disableClose: true
        });

        return dialogRef.afterClosed().pipe(
            map((result?: RegisterProxiesResultModel) => {
                if (!result) {
                    return undefined;
                }

                const proxyResults = Object.entries(result.proxyResults).map(([shareholderId, isSuccessful]) => ({
                    shareholderId,
                    isSuccessful
                }));

                const successfulResults = proxyResults.filter(r => r.isSuccessful).length;
                if (successfulResults > 0) {
                    this.translationService.getText('Gate.RegisterProxiesSuccessful', { amount: successfulResults }).subscribe(message => {
                        this.log.update(val => {
                            val.unshift(
                                new GateLogModel({
                                    actionType: GateActionTypeEnum.CheckinRepresentative,
                                    success: true,
                                    shareholderIdOrBarcode: `${result.representativeShareholderId}`,
                                    timeStamp: new Date(),
                                    message
                                })
                            );
                            return val;
                        });
                    });
                }

                proxyResults
                    .filter(r => !r.isSuccessful)
                    .forEach(({ shareholderId }) => {
                        this.translationService.getText('Gate.RegisterProxiesFailed', { shareholderId }).subscribe(message => {
                            this.log.update(val => {
                                val.unshift(
                                    new GateLogModel({
                                        actionType: GateActionTypeEnum.CheckinRepresentative,
                                        success: false,
                                        shareholderIdOrBarcode: `${result.representativeShareholderId}`,
                                        timeStamp: new Date(),
                                        message
                                    })
                                );
                                return val;
                            });
                        });
                    });

                return result;
            })
        );
    }

    private isDmi(): boolean {
        return !!this.dmiStatusIndicator.dmiStatus?.status && !this.dmiStatusIndicator.dmiStatus?.useTvs;
    }

    private askDmiCode(): Observable<OptionalType<string>> {
        const dialog = this.matDialog.open(GateDeviceCodeDialogComponent, { disableClose: true });
        return dialog.afterClosed().pipe(map(data => data?.deviceCode));
    }

    private sendRequest(barcode: string, deviceCode?: string): Observable<any> {
        const responseAction = {
            next: this.handleSuccess(barcode).bind(this),
            error: this.handleError(barcode).bind(this)
        };

        if (this.actionType === GateActionTypeEnum.Checkin || this.actionType === GateActionTypeEnum.CheckinRepresentative) {
            return this.gateResource.checkIn({ barcode, dmiDeviceCode: deviceCode }).pipe(tap(responseAction));
        }

        return this.gateResource.checkOut(barcode).pipe(tap(responseAction));
    }

    private handleSuccess(barcode: string) {
        return (response: ApiResponseModel<PostValueResponseModel<GmAdmissionModel>>) => {
            this.log.update(val => {
                val.unshift(
                    new GateLogModel({
                        actionType: this.actionType,
                        success: response.value.result.isSuccessful,
                        shareholderIdOrBarcode: response.value.result.shareholderId.toString() ?? barcode,
                        timeStamp: new Date(),
                        message: response.value.message
                    })
                );
                return val;
            });

            // return to normal checkin automatically
            this.actionType = GateActionTypeEnum.Checkin;
        };
    }

    private handleError(barcode: string) {
        return (response: { error: { value: ApiResponseModel<PostValueResponseModel<null>> } }) => {
            this.log.update(val => {
                val.unshift(
                    new GateLogModel({
                        actionType: this.actionType,
                        success: false,
                        shareholderIdOrBarcode: barcode,
                        timeStamp: new Date(),
                        message: response.error.value?.message
                    })
                );
                return val;
            });
        };
    }

    private closeSwalDialog(): void {
        Swal.close();
    }
}
